import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Flex } from '@magnetic/flex';
import { useTranslation } from 'react-i18next';
import RoomFinder from '../room-finder';
import { store } from '../../../../../store/store';
import { getRoomFinderStatus, setRoomFinderOpen, setShowOnMap, } from '../../../../../store/uislice';
import { useSelector } from 'react-redux';
import { getContext, getSelectedLocation, selectFloorById, } from '../../../redux/spaces-slice';
import { closeRoomDetailsPopup } from '../room-details-popup';
const Text = styled.div `
  font-size: ${(props) => props.fontSize}vw;
`;
const ArrowUp = styled.div `
  width: 0;
  height: 0;
  border-left: 0.8vw solid transparent;
  border-right: 0.8vw solid transparent;
  border-bottom: 0.8vw solid #61dc2a;
  margin-left: 2vw;
  margin-bottom: -1.4vw;
  margin-top: 1vw;
`;
const RoomCountsContainer = styled(Flex) `
  min-height: fit-content;
  padding: 0.6vw;
  width: 16.8vw;
  margin-left: 0.4vw;
  background: #1f2226;
  border-radius: 0.8vw;
`;
const CountBoxContainer = styled(Flex) `
  border: 1.6px solid ${(props) => props.borderColor};
  height: fit-content;
  padding: 0.24vw;
  width: 4.2vw;
  min-width: fit-content;
  border-radius: 0.8vw;
  background: ${(props) => props.backgroundColor};
  text-align: center;
  margin-top: 0.4vw;
`;
const TotalCount = styled.div `
  font-size: 3.2vw;
  font-family: 'CiscoSans Thin';
`;
const Button = styled(Flex) `
  height: 2.2vw;
  background: ${(props) => props.background};
  border-radius: 0.6vw;
  text-align: center;
  font-size: 1.2vw;
  padding-top: 1vw;
  margin: 1.4vw 0vw;
  z-index: 9999;
  cursor: pointer;
`;
const CountBox = ({ t, status, count }) => {
    const backgroundColorMap = {
        Available: '#4D7E3A',
        Booked: '#8B390E',
        Occupied: '#A5314B',
        NoData: '#1f2226',
    };
    const borderColorMap = {
        Available: '#71FF31',
        Booked: '#FF8F54',
        Occupied: '#FCA5B8',
        NoData: '#abacad',
    };
    const localizedStatus = {
        Available: t('floor.counts-widget.available'),
        Booked: t('floor.counts-widget.booked'),
        Occupied: t('floor.counts-widget.occupied'),
        NoData: t('floor.counts-widget.no-data'),
    };
    return (_jsxs(CountBoxContainer, Object.assign({ direction: "vertical", backgroundColor: backgroundColorMap[status], borderColor: borderColorMap[status] }, { children: [_jsx(Text, Object.assign({ fontSize: 1.4 }, { children: count })), _jsx(Text, Object.assign({ fontSize: 0.72 }, { children: localizedStatus[status] }))] })));
};
const TotalInfo = ({ t, forRooms, count }) => {
    const text1 = forRooms
        ? t('floor.counts-widget.meeting-rooms')
        : t('floor.counts-widget.desks-on-this');
    const text2 = forRooms
        ? t('floor.counts-widget.on-this-floor')
        : t('floor.counts-widget.floor');
    return (_jsxs(Flex, { children: [_jsx(TotalCount, { children: count }), _jsxs(Flex, Object.assign({ style: { margin: '0.72vw' }, direction: "vertical" }, { children: [_jsx(Text, Object.assign({ fontSize: 1 }, { children: text1 })), _jsx(Text, Object.assign({ fontSize: 1 }, { children: text2 }))] }))] }));
};
const RoomFinderButton = ({ t, roomFinderButtonHandler }) => {
    return (_jsx(Button, Object.assign({ id: "room-finder-button", background: "linear-gradient(180deg, #1D7FF3 0%, #113D7D 100%)", direction: "vertical", onClick: roomFinderButtonHandler }, { children: t('floor.counts-widget.room-finder') })));
};
const SeeAvailableDesksButton = ({ t }) => {
    const seeAvailableDesksHandler = () => {
        store.dispatch(setShowOnMap({ payload: 'Desks' }));
    };
    return (_jsxs(_Fragment, { children: [_jsx(ArrowUp, {}), _jsx(Button, Object.assign({ onClick: () => seeAvailableDesksHandler(), background: "linear-gradient(180deg, #61DC2A 0%, #3D6D27 100%)", direction: "vertical" }, { children: t('floor.counts-widget.see-available-desks') }))] }));
};
const CountsWidget = ({ floorId, forRooms }) => {
    var _a, _b, _c, _d, _e, _f;
    const { t } = useTranslation();
    const floorLocationId = useSelector(getSelectedLocation);
    const showRoomFinder = useSelector(getRoomFinderStatus);
    const contextInfo = useSelector(getContext);
    const interactive = (_a = contextInfo === null || contextInfo === void 0 ? void 0 : contextInfo.interactive) !== null && _a !== void 0 ? _a : true;
    const floorAnalytics = useSelector((state) => selectFloorById(state, floorLocationId));
    useEffect(() => {
        if (window.enableDebugLogs) {
            console.log('Floor view :: Floor analytics', floorAnalytics);
        }
    }, [floorAnalytics]);
    const availabilityInfo = forRooms
        ? (_b = floorAnalytics === null || floorAnalytics === void 0 ? void 0 : floorAnalytics.availabilityInfo) === null || _b === void 0 ? void 0 : _b.roomInfo
        : (_c = floorAnalytics === null || floorAnalytics === void 0 ? void 0 : floorAnalytics.availabilityInfo) === null || _c === void 0 ? void 0 : _c.deskInfo;
    const availableCount = (_d = availabilityInfo === null || availabilityInfo === void 0 ? void 0 : availabilityInfo.availableCount) !== null && _d !== void 0 ? _d : 0;
    const bookedCount = (_e = availabilityInfo === null || availabilityInfo === void 0 ? void 0 : availabilityInfo.bookedCount) !== null && _e !== void 0 ? _e : 0;
    const occupiedCount = (_f = availabilityInfo === null || availabilityInfo === void 0 ? void 0 : availabilityInfo.occupiedCount) !== null && _f !== void 0 ? _f : 0;
    const roomFinderButtonHandler = () => {
        closeRoomDetailsPopup();
        store.dispatch(setRoomFinderOpen({ payload: true }));
    };
    return (_jsxs(RoomCountsContainer, Object.assign({ direction: "vertical" }, { children: [_jsx(TotalInfo, { t: t, forRooms: forRooms, count: availableCount + bookedCount + occupiedCount }), _jsxs(Flex, Object.assign({ justify: "space-between" }, { children: [_jsx(CountBox, { t: t, status: "Available", count: availableCount }), _jsx(CountBox, { t: t, status: "Booked", count: bookedCount }), _jsx(CountBox, { t: t, status: "Occupied", count: occupiedCount })] })), forRooms ? (showRoomFinder ? (_jsx(RoomFinder, { floorId: floorLocationId, onClose: () => store.dispatch(setRoomFinderOpen({ payload: false })) })) : (interactive && (_jsx(RoomFinderButton, { t: t, roomFinderButtonHandler: roomFinderButtonHandler })))) : (_jsx(SeeAvailableDesksButton, { t: t }))] })));
};
export default CountsWidget;
